import { FieldValidationType } from '../enums/FieldValidationType';
import { ClientField, ClientFieldsGroupedByValidationType } from '../types/ClientField';
import {
    ContactAddress,
    ContactField,
    ContactFieldsGroupedByValidationType,
} from '../types/ContactField';
import { ContactOwner, UsersByOwnership } from '../types/ContactOwner';
import { ContactTag, SectionTag } from '../types/ContactTag';
import { UserInfo } from '../types/UserConfig';

interface HasDate {
    date: Date;
}

export function GetLowestDate<T extends HasDate>(items: T[]) {
    return items.reduce(
        (prev, current) => {
            return prev.date > current.date ? prev : current;
        },
        {
            date: new Date(-8640000000000000),
        },
    ).date;
}

export function groupContactFieldsByValidationType(contactFields: ContactField[]) {
    return contactFields.reduce(
        (grouped: ContactFieldsGroupedByValidationType, field: ContactField) => {
            switch (field.validationType) {
                case FieldValidationType.address:
                    grouped.address.push(field as ContactField<ContactAddress>);
                    break;
                case FieldValidationType.date:
                    grouped.date.push(field as ContactField<string>);
                    break;
                case FieldValidationType.dollars:
                    grouped.dollar.push(field as ContactField<string>);
                    break;
                case FieldValidationType.email:
                    grouped.email.push(field as ContactField<string>);
                    break;
                case FieldValidationType.none:
                    grouped.none.push(field as ContactField<string>);
                    break;
                case FieldValidationType.other:
                    grouped.other.push(field as ContactField<string>);
                    break;
                case FieldValidationType.usMobilePhone:
                    grouped.phone.push(field as ContactField<string>);
                    break;
                case FieldValidationType.years:
                    grouped.year.push(field as ContactField<string>);
                    break;
                default:
                    break;
            }
            return grouped;
        },
        {
            address: [],
            date: [],
            dollar: [],
            email: [],
            none: [],
            other: [],
            phone: [],
            year: [],
        },
    );
}

export function groupClientFieldsByValidationType(
    clientFields: ClientField[],
    restrict: boolean = true,
) {
    return clientFields.reduce(
        (grouped: ClientFieldsGroupedByValidationType, field: ClientField) => {
            //this sucks. TODO: fix the client fields api to return readonly or some flag...
            //3 is tms code, 20 is day phone, 21 is evening phone
            if (!restrict || !field.isReadOnly) {
                switch (field.validationType) {
                    case FieldValidationType.address:
                        grouped.address.push(field);
                        break;
                    case FieldValidationType.date:
                        grouped.date.push(field);
                        break;
                    case FieldValidationType.dollars:
                        grouped.dollar.push(field);
                        break;
                    case FieldValidationType.email:
                        grouped.email.push(field);
                        break;
                    case FieldValidationType.none:
                        grouped.none.push(field);
                        break;
                    case FieldValidationType.other:
                        grouped.other.push(field);
                        break;
                    case FieldValidationType.usMobilePhone:
                        grouped.phone.push(field);
                        break;
                    case FieldValidationType.years:
                        grouped.year.push(field);
                        break;
                    default:
                        break;
                }
            }
            return grouped;
        },
        {
            address: [],
            date: [],
            dollar: [],
            email: [],
            none: [],
            other: [],
            phone: [],
            year: [],
        },
    );
}

export const groupUsersByOwnership = (
    users: UserInfo[],
    owners: ContactOwner[],
): UsersByOwnership => {
    return users.reduce(
        (acc: UsersByOwnership, user) => {
            if (owners.find(anOwner => anOwner.isPrimary && anOwner.userName === user.userName)) {
                return acc;
            } else if (
                owners.find(anOwner => !anOwner.isPrimary && anOwner.userName === user.userName)
            ) {
                acc.followers.push({ ...user, flag: 'follower' });
            } else {
                acc.unassigned.push({ ...user, flag: 'unassigned' });
            }
            return acc;
        },
        {
            followers: [],
            unassigned: [],
        },
    );
};

export const groupTagsByAssignment = (
    userTags: ContactTag[],
    selectedTags: ContactTag[],
    search: string,
): SectionTag => {
    const searchedTags = userTags.filter(tag =>
        tag.tag.toLowerCase().includes(search.toLowerCase()),
    );

    return searchedTags.reduce(
        (acc: SectionTag, tag) => {
            if (selectedTags.find(selected => selected.tag === tag.tag)) {
                acc.assigned.push({ ...tag, flag: 'assigned' });
            } else {
                acc.unassigned.push({ ...tag, flag: 'unassigned' });
            }
            return acc;
        },
        {
            assigned: [],
            unassigned: [],
        },
    );
};
